import { useConfigStore } from '@/stores/ConfigStore'
import { useCardStore } from '@/stores/CardStore'
import { useUserStore } from '@/stores/UserStore'
import { getFromLocalStorageByUserId } from '@/helpers/general'
import { extractBaseParams, buildSearchParams } from '@/helpers/filters'
import {
  FILTERS_BLOCK_1,
  CATEGORIES_FILTER_ITEM,
  BATCH_FILTER_ITEM,
  BRAND_FILTER_ITEM,
  ATTRIBUTE_FILTER_ITEM,
  GRADED_FILTER_ITEM,
  GRADE_FILTER_ITEM,
  INVENTORY_FILTERS_OPTIONS,
  UNIFIED_STATUS_TAB_OPTIONS,
  UNIFIED_COLUMN_OPTIONS,
  UNIFIED_COLUMN_ALIASES,
  INVENTORY_CARD_COLUMN_OPTIONS,
  INVENTORY_SORT_OPTIONS,
  YEAR_FILTER_ITEM,
  BOX_FILTER_ITEM,
  ROW_FILTER_ITEM,
  SHELF_FILTER_ITEM,
  CABINET_FILTER_ITEM,
  TEAM_FILTER_ITEM,
  SET_FILTER_ITEM,
  LOCATION_FILTER_ITEM,
  CUSTOM_FIELDS_FILTER_ITEM,
  QUANTITY_FILTER_ITEM,
  RESTOCKABLE_FILTER_ITEM,
  BATCH_DETAIL_FILTERS,
  BATCH_DETAIL_UNREVIEWED_FILTER,
  BASE_PARAM_KEYS
 } from '@/constants'

/**
 * Status tabs above the Inventory grid. New design shows the unified 5-state
 * set (the same states the Status column renders, and the same set the
 * batch-in-inventory grid shows); the classic view keeps its own list.
 */
export const inventoryStatusTabOptions = () =>
  useUserStore().isNewDesign ? UNIFIED_STATUS_TAB_OPTIONS : INVENTORY_FILTERS_OPTIONS

/**
 * Default column options (the Columns dropdown + the grid's visibility map) for
 * the Inventory grid. New design uses the unified list — every batch AND
 * inventory column, all visible; a user's saved unchecks are layered back on by
 * `mergeColumnOptions`.
 */
export const inventoryDefaultColumnOptions = () =>
  useUserStore().isNewDesign ? UNIFIED_COLUMN_OPTIONS : INVENTORY_CARD_COLUMN_OPTIONS

/**
 * The sort/columns a "reset" returns to.
 *
 * With a saved view open that's the VIEW's own sort/columns — resetting there
 * means "back to how this view was saved", not "back to factory". Without one
 * it's the app defaults. Both chips and `SaveViews.handleReset` answer the same
 * question, so it's answered once, here.
 */
export const inventoryBaselineSort = (selectedView = null) =>
  selectedView?.id && selectedView.sort ? selectedView.sort : INVENTORY_SORT_OPTIONS[1]

export const inventoryBaselineColumns = (selectedView = null) =>
  selectedView?.id && selectedView.columns?.length
    ? mergeColumnOptions(selectedView.columns, inventoryDefaultColumnOptions())
    : inventoryDefaultColumnOptions()

/** Has the user moved off that baseline? Drives whether a chip shows at all. */
export const isInventorySortChanged = (sortOption, selectedView = null) =>
  (sortOption?.value ?? inventoryBaselineSort(selectedView).value) !== inventoryBaselineSort(selectedView).value

/**
 * Columns differ when their VISIBILITY differs — order and label changes don't
 * count, and a saved list can legitimately be missing columns added since.
 */
export const isInventoryColumnsChanged = (columnOptions = [], selectedView = null) => {
  if (!columnOptions?.length) return false

  const baseline = inventoryBaselineColumns(selectedView)
  const baseByField = new Map(baseline.map(col => [col.field, col.checked !== false]))

  return columnOptions.some(col => {
    const baseChecked = baseByField.get(col.field)
    return baseChecked !== undefined && baseChecked !== (col.checked !== false)
  })
}

export const countVisibleColumns = (columnOptions = []) =>
  columnOptions.filter(col => col.checked !== false).length

/**
 * Rewrite saved column options onto the unified column ids. Three legacy
 * inventory fields never matched a real column ('name', 'inventoryStatus',
 * 'sale_price'), so without this a user's saved visibility silently resets.
 * De-dupes, since two legacy fields can alias onto the same column.
 */
export const normalizeColumnOptionFields = (columns = []) => {
  // Old design still renders the legacy columns, whose ids ARE those fields.
  if (!columns?.length || !useUserStore().isNewDesign) return columns

  const seen = new Set()

  return columns.reduce((acc, col) => {
    const field = UNIFIED_COLUMN_ALIASES[col.field] || col.field
    if (seen.has(field)) return acc

    seen.add(field)
    acc.push({ ...col, field })
    return acc
  }, [])
}

/**
 * Merge a saved display-columns list onto the current defaults. Every column now
 * ships visible, so this is what keeps that from overriding people:
 *   - a column the user unchecked stays unchecked (their state wins),
 *   - columns added since they last saved arrive with the default state (visible),
 *   - columns that no longer exist are dropped.
 * Saved order is preserved; new columns append. Label/eBay/shopify metadata always
 * comes from the default, so renames propagate.
 *
 * Custom fields are NOT handled here — `DisplayMenu` merges those separately from
 * the config, then concatenates.
 */
export const mergeColumnOptions = (saved = [], defaults = []) => {
  const savedColumns = normalizeColumnOptionFields(saved) || []
  const defaultByField = new Map(defaults.map(col => [col.field, col]))

  const kept = savedColumns.reduce((acc, col) => {
    const def = defaultByField.get(col.field)
    if (!def) return acc

    acc.push({ ...def, checked: col.checked !== false })
    return acc
  }, [])

  const keptFields = new Set(kept.map(col => col.field))

  return [...kept, ...defaults.filter(col => !keptFields.has(col.field))]
}

export const getInventoryStatus = (card) => {
  if (!card) return 'available'

  if (card.status === 'draft') {
    return 'draft'
  }

  const quantity = Number(card.quantity)
  const isOutOfStock = Number.isFinite(quantity) && quantity <= 0

  if (isOutOfStock && card.restockable) {
    return 'out_of_stock'
  }

  if (isOutOfStock && !card.restockable) {
    return 'sold_out'
  }

  return 'available'
}

// A card is "Listed" when at least one marketplace reports it listed (checks
// both `<channel>ListedStatus` — the field the publish/listing flow uses — and
// `<channel>_status`, across every marketplace).
const LISTING_CHANNELS = ['collx', 'ebay', 'whatnot', 'shopify']
export const isCardListed = (card) =>
  !!card && LISTING_CHANNELS.some(k =>
    card[`${k}ListedStatus`] === 'listed' || card[`${k}_status`] === 'listed')

// Unified display status for the NEW-DESIGN status control — the SAME everywhere
// (batch grid, inventory grid, inventory details). Only `draft` / `ready_to_list`
// are user-selectable; `listed` / `out_of_stock` / `sold_out` are system-set and
// shown read-only. (`available` from getInventoryStatus == `ready_to_list` here.)
export const getCardStatusDisplay = (card) => {
  if (!card) return 'ready_to_list'
  if (card.status === 'draft') return 'draft'
  if (card.quantity <= 0 && card.restockable) return 'out_of_stock'
  if (card.quantity <= 0 && !card.restockable) return 'sold_out'
  if (isCardListed(card)) return 'listed'
  return 'ready_to_list'
}

const getTemplate_ = ({ templateField, templateId }) => {
  const configStore = useConfigStore()
  const cardStore = useCardStore()

  let template 
  for(let prop in configStore.config[templateField]) {
    if (configStore.config[templateField][prop].id === cardStore.selectedCardData[templateId]) {
      template = configStore.config[templateField][prop]
    }
  }

  return template?.name || '{Template Name}'
}

export const getTemplateName = ({ mainField, templateField, templateId, template, selectedTemplate}) => { 
  const cardStore = useCardStore()
  if (selectedTemplate) {
    return selectedTemplate.name
  }

  if (!cardStore.selectedCardData[mainField] && !cardStore.selectedCardData[templateId] && cardStore.selectedCardData[template]) {
    return 'Default CDP Template'
  }
  if (cardStore.selectedCardData[mainField] && !cardStore.selectedCardData[templateId]) {
    return `Custom ${mainField === 'description' ? 'Description' : 'Title'}`
  }

  if (cardStore.selectedCardData[templateId]) {
    return getTemplate_({ templateField, templateId })
  }

  if (!cardStore.selectedCardData[mainField] && !cardStore.selectedCardData[templateId] && !cardStore.selectedCardData[template]) {
    return '{Template Name}'
  }
}

export const getAvailableFilters = () => {
  const configStore = useConfigStore()
  const cardStore = useCardStore()

  let brandPresent = false
  let attributesPresent = false
  let yearPresent = false
  let boxPresent = false
  let rowPresent = false
  let shelfPresent = false
  let cabinetPresent = false
  let teamPresent = false
  let setPresent = false
  const customFieldsPresent = configStore.config?.customFields?.length > 0

  const availableFilters = [
    CATEGORIES_FILTER_ITEM
  ]

  for(const item of cardStore.cards) {
    if (item.attributes && !attributesPresent) {
      availableFilters.push(ATTRIBUTE_FILTER_ITEM)
      attributesPresent = true
    }
    
    if (item.year && !yearPresent) {
      availableFilters.push(YEAR_FILTER_ITEM)
      yearPresent = true
    }

    if (item.set && !setPresent) {
      availableFilters.push(SET_FILTER_ITEM)
      setPresent = true
    }

    if (item.brand && !brandPresent) {
      availableFilters.push(BRAND_FILTER_ITEM)
      brandPresent = true
    }

    if (item.team && !teamPresent) {
      availableFilters.push(TEAM_FILTER_ITEM)
      teamPresent = true
    }

    if (item.box && !boxPresent) {
      boxPresent = true
    }

    if (item.row && !rowPresent) {
      rowPresent = true
    }

    if (item.shelf && !shelfPresent) {
      shelfPresent = true
    }

    if (item.cabinet && !cabinetPresent) {
      cabinetPresent = true
    }
  }

  availableFilters.push(QUANTITY_FILTER_ITEM)

  // Location filter with only the sub-filters that are present (Box, Row, Shelf, Cabinet)
  const locationChildren = []
  
  if (boxPresent) locationChildren.push(BOX_FILTER_ITEM)
  if (rowPresent) locationChildren.push(ROW_FILTER_ITEM)
  if (shelfPresent) locationChildren.push(SHELF_FILTER_ITEM)
  if (cabinetPresent) locationChildren.push(CABINET_FILTER_ITEM)
  if (locationChildren.length > 0) {
    availableFilters.push({
      ...LOCATION_FILTER_ITEM,
      children: locationChildren
    })
  }

  // CDP-2435 — Batch filter: only surface when the backend reports batches with ≥1 item.
  // unshift → it lands first in `availableFilters`, i.e. directly after Price (the last
  // item of FILTERS_BLOCK_1) in the concatenated menu list.
  const hasBatches = cardStore.availableFilters?.batch?.length > 0
  if (hasBatches) {
    availableFilters.unshift(BATCH_FILTER_ITEM)
  }

  const hasGradedCards = cardStore.availableFilters?.graded?.some(item => (item.value === true || item.value === 1) && item.count > 0)
  if (hasGradedCards) {
    availableFilters.push(GRADED_FILTER_ITEM)
    availableFilters.push(GRADE_FILTER_ITEM)
  }

  if (customFieldsPresent) {
    availableFilters.push(CUSTOM_FIELDS_FILTER_ITEM)
  }

  availableFilters.push(RESTOCKABLE_FILTER_ITEM)


  return {
    status_tabs: {
      selected: 'All',
      options: inventoryStatusTabOptions(),
    },
    filter_blocks: [
      [...FILTERS_BLOCK_1, ...availableFilters],
    ]
  }
}

/**
 * Check if duplication popup is dismissed with 24-hour expiration
 * @param {number} userId - The user ID
 * @returns {boolean} - True if popup should be shown, false if dismissed and not expired
 */
export const checkDismissedDuplication = (userId) => {
  const storageKey = 'duplicationPopupDismissed'
  const dismissedData = sessionStorage.getItem(storageKey)
  
  if (!dismissedData) {
    return true // Show popup if no dismissed data exists
  }
  
  try {
    const parsed = JSON.parse(dismissedData)
    const now = Date.now()
    const twentyFourHours = 24 * 60 * 60 * 1000 // 24 hours in milliseconds
    
    // Check if user has dismissed data
    if (!parsed[userId]) {
      return true // Show popup if user has no dismissed data
    }
    
    const userData = parsed[userId]
    
    // Check if 24 hours have passed
    if (now - userData.timestamp > twentyFourHours) {
      // Remove expired entry for this user
      delete parsed[userId]
      sessionStorage.setItem(storageKey, JSON.stringify(parsed))
      return true // Show popup after expiration
    } else {
      return !userData.dismissed // Don't show if dismissed and not expired
    }
  } catch (error) {
    // Handle legacy format (just 'true' string or old format)
    if (typeof dismissedData === 'string' && dismissedData === 'true') {
      // Convert legacy format to new format
      const newData = {
        [userId]: {
          dismissed: true,
          timestamp: Date.now()
        }
      }
      sessionStorage.setItem(storageKey, JSON.stringify(newData))
      return false // Don't show for legacy dismissed state
    }
    
    return true // Show popup if data is corrupted
  }
}

/**
 * Set duplication popup as dismissed with 24-hour expiration
 * @param {number} userId - The user ID
 */
export const setDismissedDuplication = (userId) => {
  const storageKey = 'duplicationPopupDismissed'
  const existingData = sessionStorage.getItem(storageKey)
  
  let dismissedData = {}
  
  if (existingData) {
    try {
      dismissedData = JSON.parse(existingData)
    } catch (error) {
      // If corrupted, start fresh
      dismissedData = {}
    }
  }
  
  // Add or update user's dismissal data
  dismissedData[userId] = {
    dismissed: true,
    timestamp: Date.now()
  }
  
  sessionStorage.setItem(storageKey, JSON.stringify(dismissedData))
}

/**
 * Get length skipped items where we have isHeader
 * Was prepared to show grouped duplicates - gray full width row in inventory list
 * @param {number} userId - The user ID
 */
export const getInventoryCardLengthWithoutDuplicatesHeader = (card) => {
  const cardStore = useCardStore()
  
  return cardStore.duplicateLength
  // const cardStore = useCardStore()
  // const cardLength = cardStore.cards.length
  // const duplicatesHeaderLength = cardStore.cards.filter(card => card.isHeader).length
  // return cardLength - duplicatesHeaderLength
}

export const getIsDisabled = (status, isErrors) => {
  const cardStore = useCardStore()
  // Sold out/out of stock statuses can't change to sold out/out of stock/available
  return (['sold_out', 'out_of_stock'].includes(cardStore.selectedCardData.inventoryStatus) && 
    ['sold_out', 'out_of_stock', 'available'].includes(status)) ||
    
    // Available status can't change to sold out/out of stock
    (['available'].includes(cardStore.selectedCardData.inventoryStatus) && 
     ['sold_out', 'out_of_stock'].includes(status)) ||
    
    // Draft status can't change to sold out/out of stock
    (['draft'].includes(cardStore.selectedCardData.inventoryStatus) && 
     ['sold_out', 'out_of_stock'].includes(status)) ||
    
    // Available status with errors can't be selected
    (['available'].includes(status) && 
     (isErrors.collx || isErrors.ebay || isErrors.whatnot || isErrors.shopify))
}

export const getStatusItemClasses = (active, status, isErrors) => {
  const baseClasses = [
    active ? 'bg-tint-1' : 'text-gray-900',
    'cursor-pointer select-none relative py-2 pl-3 pr-9'
  ]

  // Check if status should be disabled
  const isDisabled = getIsDisabled(status, isErrors)
    

  if (isDisabled) {
    baseClasses.push('bg-gray-100 pointer-events-none cursor-default')
  }

  return baseClasses
}

/**
 * Build the query string shared by the inventory list + market-prices endpoints.
 * Pure: takes the current filters/page/user rather than reaching into stores, so
 * `fetchInventory` and `fetchMarketPrices` (InventoryStore) can both reuse it.
 *
 * @param {Object}  args
 * @param {Object}  args.itemFilters - cardStore.itemFilters
 * @param {number}  args.page        - cardStore.page
 * @param {number|string} args.userId - userStore.user.id (for perPage/sort localStorage)
 * @param {number}  [args.perPage=25]
 * @returns {{ query: string, perPage_: number }}
 */
export const buildInventoryQuery = ({ itemFilters = {}, page = 1, userId, perPage = 25 }) => {
  const perPage_ = getFromLocalStorageByUserId('perPage_inventory', userId) || perPage

  let sortBy_ = ''
  if (!itemFilters.sortBy) {
    const savedSort = getFromLocalStorageByUserId('inventorySort', userId)
    sortBy_ = savedSort?.value || 'created_at.desc'
  }

  // Extract base params from itemFilters using constants
  const { baseParams: extractedBaseParams, filterParams: remainingFilters } = extractBaseParams(
    itemFilters,
    BASE_PARAM_KEYS
  )

  const baseParams = {
    page,
    perPage: perPage_,
    sortBy: extractedBaseParams.sortBy || sortBy_,
    ...extractedBaseParams,
  }

  // Combine remaining filters into a single comma-separated filter param
  const filterParams = Object.entries(remainingFilters).length
    ? `filter=${Object.entries(remainingFilters)
        .map(([key, value]) => `${key}=${value}`)
        .join(',')}`
    : ''

  const query = `${filterParams ? '?' + filterParams : ''}${
    Object.keys(baseParams).length ? (filterParams ? '&' : '?') + buildSearchParams({
      ...baseParams,
      ...(itemFilters.q ? { q: itemFilters.q } : {})
    }) : ''
  }`

  return { query, perPage_ }
}

/**
 * Attach a market-price recommendation to a single card by id.
 * `marketPrices` shape: `{ [cardId]: { suggested_price, last_updated } }`.
 * Returns a NEW card object with `price_recommendation` set when there's a match,
 * otherwise the card unchanged. Pure — safe for both list + detail surfaces.
 */
export const applyMarketPriceToCard = (card, marketPrices = {}) => {
  const recommendation = card && marketPrices?.[card.id]
  return recommendation
    ? { ...card, has_price_recommendation: true, price_recommendation: recommendation }
    : card
}

/**
 * Merge market-price recommendations into a list of inventory cards, keyed by id.
 * Returns a NEW array (immutable — keeps AG Grid's getRowId diffing happy).
 */
export const applyMarketPricesToCards = (cards = [], marketPrices = {}) => {
  if (!marketPrices || !Object.keys(marketPrices).length) return cards
  return cards.map((card) => applyMarketPriceToCard(card, marketPrices))
}

/**
 * Filter config for the NEW-DESIGN card grids — Inventory and batch-in-inventory
 * both. The standard inventory list (both surfaces read the same endpoint, so
 * the same `available_filters` are real on each) plus Unreviewed · Missing
 * Details · Suggested Edits in a second block: the menu renders one block per
 * section with a divider between, so the trio lands at the bottom.
 *
 * Legacy Inventory keeps `getAvailableFilters()` — the trio is a new-design row.
 */
export const getCardGridFilters = () => {
  const filters = getAvailableFilters()

  return {
    ...filters,
    // Kept so `handleFilterSelection` can resolve a row by `fastActionIndex`.
    fast_actions: BATCH_DETAIL_FILTERS.fast_actions,
    filter_blocks: [
      ...filters.filter_blocks,
      [BATCH_DETAIL_UNREVIEWED_FILTER, ...BATCH_DETAIL_FILTERS.fast_actions],
    ],
  }
}

/**
 * Move one entry of a column-options list, returning a NEW array.
 *
 * Keyed by FIELD, not index: the dropdown hides rows that don't apply (the
 * sports-only trio on a non-sports batch), so a rendered index and an index into
 * this array are not the same number. Fields can't drift.
 */
export const reorderColumnOptions = (options = [], fromField, toField) => {
  if (!fromField || !toField || fromField === toField) return options

  const from = options.findIndex(option => option.field === fromField)
  const to = options.findIndex(option => option.field === toField)
  if (from < 0 || to < 0) return options

  const next = [...options]
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)

  return next
}

/**
 * Push a column-options order onto an AG Grid instance.
 *
 * Only the columns the dropdown actually manages are reordered. Structural ones
 * — the row-flag gutter, the checkbox, the thumbnails — aren't in
 * `columnOptions` at all, and a naive `applyColumnState({ applyOrder: true })`
 * with a partial list would shove every unlisted column to the END, throwing the
 * pinned columns to the right-hand side.
 *
 * So: take the grid's current state, treat the slots held by managed columns as
 * holes, and refill those holes in the requested order. Unmanaged columns keep
 * the exact positions they already had.
 */
export const applyColumnOrderToGrid = (gridApi, fields = []) => {
  if (!gridApi || typeof gridApi.getColumnState !== 'function') return
  if (typeof gridApi.isDestroyed === 'function' && gridApi.isDestroyed()) return

  const state = gridApi.getColumnState()
  if (!state?.length) return

  const managed = new Set(fields)
  const byId = new Map(state.map(col => [col.colId, col]))

  // Requested order, limited to columns this grid actually has.
  const queue = fields.filter(field => byId.has(field)).map(field => byId.get(field))
  if (!queue.length) return

  let next = 0
  const ordered = state.map(col => (managed.has(col.colId) ? queue[next++] : col))

  gridApi.applyColumnState({ state: ordered, applyOrder: true })
}

/**
 * Reorder a column-options list to match the grid's current column order.
 *
 * The inverse of `applyColumnOrderToGrid`: dragging a column header is the other
 * way to author the order, and the dropdown has to agree with what the
 * spreadsheet shows — otherwise the two surfaces each claim a different order.
 *
 * Fields the grid doesn't have (hidden-by-page options) keep their relative
 * order at the end. Returns the ORIGINAL array when nothing moved, so callers
 * can use identity to skip a write and avoid a store→grid→store loop.
 */
export const columnOptionsInGridOrder = (options = [], orderedFields = []) => {
  const rank = new Map(orderedFields.map((field, index) => [field, index]))

  const inGrid = options.filter(option => rank.has(option.field))
  const notInGrid = options.filter(option => !rank.has(option.field))

  const next = [
    ...inGrid.sort((a, b) => rank.get(a.field) - rank.get(b.field)),
    ...notInGrid,
  ]

  const unchanged = next.every((option, index) => option.field === options[index].field)

  return unchanged ? options : next
}
